import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import {
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import {
  buildGuaranteesApplicationAmApprovalLetterManualSignPath,
  getStringValue,
} from '~/modules/guarantees/utils.ts';
import { isGuaranteesApprovalLetterPaperExecutionCheck } from '../../shared/approvalLetterExecution.ts';
import {
  selectGuaranteesAmTemplateDocumentError,
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
  selectGuaranteesMainDealConditions,
  selectGuaranteesMainDealConditionsError,
  selectGuaranteesMainDealConditionsStatus,
  selectGuaranteesManagerSupervisorInfoStatus,
  selectGuaranteesPatchMainDealConditionsError,
  selectGuaranteesPatchMainDealConditionsStatus,
  selectGuaranteesSubmitApprovalLetterByAmManagerError,
  selectGuaranteesSubmitApprovalLetterByAmManagerStatus,
  selectGuaranteesSubmitApplicationByAmManagerError,
  selectGuaranteesSubmitApplicationByAmManagerStatus,
  selectHasGuaranteesAmAccess,
} from '~/store/guarantees/selectors.ts';
import {
  fetchGuaranteesAmTemplateDocument,
  fetchGuaranteesApplication,
  fetchGuaranteesMainDealConditions,
  patchGuaranteesMainDealConditions,
  submitGuaranteesApprovalLetterByAmManager,
  submitGuaranteesApplicationByAmManager,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  GuaranteesApplicationStatus,
  GuaranteesCoverageType,
  GuaranteesFetchStatus,
  type TPatchGuaranteesMainDealConditionsPayload,
} from '~/store/guarantees/types.ts';
import { normalizeGuaranteesApplicationStatus } from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import { getCurrentRouteGuaranteesApplication } from '../../shared/utils/applicationIdentity.ts';
import {
  MANAGER_APPROVAL_LETTER_COVERED_COVERAGE_TYPE_OPTION,
  MANAGER_APPROVAL_LETTER_COVERAGE_TYPE_OPTIONS,
  MANAGER_APPROVAL_LETTER_ACCESS_DENIED_TEXT,
  MANAGER_APPROVAL_LETTER_GET_STARTED_PATH,
  MANAGER_APPROVAL_LETTER_TASKS_PATH,
  MANAGER_APPROVAL_LETTER_TEXT,
} from './constants.ts';
import { isManagerApprovalLetterManualSignStatus } from '../step6/utils.ts';
import { areManagerMainDealConditionsValid } from './mainDealConditionsUtils.ts';

export const useManagerApprovalLetterPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const [coverageTypeOption, setCoverageTypeOption] = useState<TAutocompleteOption | null>(null);
  const [hasMainDealConditionsChanges, setHasMainDealConditionsChanges] = useState(false);
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const mainDealConditions = useSelector(selectGuaranteesMainDealConditions);
  const mainDealConditionsStatus = useSelector(selectGuaranteesMainDealConditionsStatus);
  const mainDealConditionsError = useSelector(selectGuaranteesMainDealConditionsError);
  const patchMainDealConditionsStatus = useSelector(selectGuaranteesPatchMainDealConditionsStatus);
  const patchMainDealConditionsError = useSelector(selectGuaranteesPatchMainDealConditionsError);
  const managerSupervisorInfoStatus = useSelector(selectGuaranteesManagerSupervisorInfoStatus);
  const hasAmAccess = useSelector(selectHasGuaranteesAmAccess);
  const amTemplateDocumentError = useSelector(selectGuaranteesAmTemplateDocumentError);
  const submitApprovalLetterByAmManagerStatus = useSelector(
    selectGuaranteesSubmitApprovalLetterByAmManagerStatus
  );
  const submitApprovalLetterByAmManagerError = useSelector(
    selectGuaranteesSubmitApprovalLetterByAmManagerError
  );
  const submitApplicationByAmManagerStatus = useSelector(
    selectGuaranteesSubmitApplicationByAmManagerStatus
  );
  const submitApplicationByAmManagerError = useSelector(
    selectGuaranteesSubmitApplicationByAmManagerError
  );
  const { currentRouteApplication, isLoadedApplicationCurrent } =
    getCurrentRouteGuaranteesApplication(application, applicationId);
  const effectiveApplicationId =
    getStringValue(currentRouteApplication?.applicationId || currentRouteApplication?.id) ||
    applicationId;
  const applicationNumber =
    getStringValue(currentRouteApplication?.applicationNumber) ||
    getStringValue(currentRouteApplication?.root?.applicationNumber) ||
    effectiveApplicationId;
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const shouldSubmitApplicationByAmManager =
    normalizedStatus === GuaranteesApplicationStatus.AWAITING_AM_APPROVAL_LETTER ||
    normalizedStatus === GuaranteesApplicationStatus.IN_PROGRESS_AM_APPROVAL_LETTER;
  const isCoverageTypeLocked =
    normalizedStatus === GuaranteesApplicationStatus.IN_PROGRESS_AM_APPROVAL_LETTER;
  const shouldRedirectToManualSignPage =
    !!currentRouteApplication &&
    isGuaranteesApprovalLetterPaperExecutionCheck(currentRouteApplication) &&
    isManagerApprovalLetterManualSignStatus(normalizedStatus);
  const redirectPath = !contragentId
    ? MANAGER_APPROVAL_LETTER_GET_STARTED_PATH
    : shouldRedirectToManualSignPage
      ? buildGuaranteesApplicationAmApprovalLetterManualSignPath(
          effectiveApplicationId,
          contragentId
        )
      : currentRouteApplication && !shouldSubmitApplicationByAmManager
        ? MANAGER_APPROVAL_LETTER_TASKS_PATH
        : '';
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const isApplicationLoading =
    (applicationStatus === GuaranteesFetchStatus.Loading && !currentRouteApplication) ||
    (!!application && !isLoadedApplicationCurrent);
  const isAmAccessLoading =
    managerSupervisorInfoStatus === GuaranteesFetchStatus.Idle ||
    managerSupervisorInfoStatus === GuaranteesFetchStatus.Loading;
  const isAmAccessDenied = !isAmAccessLoading && !hasAmAccess;
  const currentApplicationVersion =
    typeof currentRouteApplication?.version === 'number'
      ? currentRouteApplication.version
      : undefined;
  const activeSubmitByAmManagerStatus = shouldSubmitApplicationByAmManager
    ? submitApplicationByAmManagerStatus
    : submitApprovalLetterByAmManagerStatus;
  const activeSubmitByAmManagerError = shouldSubmitApplicationByAmManager
    ? submitApplicationByAmManagerError
    : submitApprovalLetterByAmManagerError;
  const isSubmittingByAmManager =
    activeSubmitByAmManagerStatus === GuaranteesFetchStatus.Loading;
  const isMainDealConditionsLoading =
    mainDealConditionsStatus === GuaranteesFetchStatus.Loading;
  const isMainDealConditionsSaving =
    patchMainDealConditionsStatus === GuaranteesFetchStatus.Loading;
  const areMainDealConditionsValid = areManagerMainDealConditionsValid(mainDealConditions);
  const selectedCoverageType = isCoverageTypeLocked
    ? GuaranteesCoverageType.COVERED
    : (coverageTypeOption?.value as GuaranteesCoverageType | undefined);
  const isSubmitDisabled =
    currentApplicationVersion === undefined ||
    !effectiveApplicationId ||
    (shouldSubmitApplicationByAmManager && !selectedCoverageType) ||
    isMainDealConditionsLoading ||
    isMainDealConditionsSaving ||
    hasMainDealConditionsChanges ||
    !areMainDealConditionsValid ||
    isSubmittingByAmManager ||
    isApplicationLoading;
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: () => navigate(MANAGER_APPROVAL_LETTER_TASKS_PATH),
      statusConfig,
    }),
    [applicationNumber, contragentId, effectiveApplicationId, navigate, statusConfig]
  );
  const handleCloseAmAccessModal = useCallback(() => {
    handleCloseAlertModal();
    navigate(MANAGER_APPROVAL_LETTER_TASKS_PATH, { replace: true });
  }, [handleCloseAlertModal, navigate]);
  const navigateToTasks = useCallback(() => {
    navigate(MANAGER_APPROVAL_LETTER_TASKS_PATH, { replace: true });
  }, [navigate]);
  const handleCoverageTypeChange = useCallback((value: TAutocompleteOption | null) => {
    setCoverageTypeOption(value);
  }, []);
  const handleMainDealConditionsDirtyChange = useCallback((isDirty: boolean) => {
    setHasMainDealConditionsChanges(isDirty);
  }, []);
  const handleMainDealConditionsSubmit = useCallback(
    async (payload: TPatchGuaranteesMainDealConditionsPayload): Promise<boolean> => {
      if (!contragentId || !effectiveApplicationId || currentApplicationVersion === undefined) {
        return false;
      }

      try {
        await dispatch(
          patchGuaranteesMainDealConditions({
            contragentId,
            applicationId: effectiveApplicationId,
            version: currentApplicationVersion,
            payload,
          })
        ).unwrap();
        setHasMainDealConditionsChanges(false);
        await dispatch(fetchGuaranteesAmTemplateDocument({ applicationId: effectiveApplicationId }));

        return true;
      } catch (error) {
        showGuaranteesRequestError(error);

        return false;
      }
    },
    [
      contragentId,
      currentApplicationVersion,
      dispatch,
      effectiveApplicationId,
      showGuaranteesRequestError,
    ]
  );
  const handleSubmitByAmManager = useCallback(async () => {
    if (!effectiveApplicationId || currentApplicationVersion === undefined) {
      return;
    }

    try {
      if (shouldSubmitApplicationByAmManager) {
        if (!selectedCoverageType) {
          return;
        }

        await dispatch(
          submitGuaranteesApplicationByAmManager({
            applicationId: effectiveApplicationId,
            coverageType: selectedCoverageType,
            version: currentApplicationVersion,
          })
        ).unwrap();
      } else {
        await dispatch(
          submitGuaranteesApprovalLetterByAmManager({
            applicationId: effectiveApplicationId,
            version: currentApplicationVersion,
          })
        ).unwrap();
      }

      setModalSuccess({
        text: MANAGER_APPROVAL_LETTER_TEXT.submitSuccessMessage,
        buttonValue: MANAGER_APPROVAL_LETTER_TEXT.successCloseButton,
        onClick: navigateToTasks,
        onClose: navigateToTasks,
      });
    } catch (error) {
      showGuaranteesRequestError(error);
    }
  }, [
    currentApplicationVersion,
    dispatch,
    effectiveApplicationId,
    navigateToTasks,
    selectedCoverageType,
    setModalSuccess,
    showGuaranteesRequestError,
    shouldSubmitApplicationByAmManager,
  ]);

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
    void dispatch(fetchGuaranteesMainDealConditions({ contragentId, applicationId }));
    void dispatch(fetchGuaranteesAmTemplateDocument({ applicationId }));
  }, [applicationId, contragentId, dispatch]);

  useFreshGuaranteesRequestError(
    applicationError ||
      mainDealConditionsError ||
      patchMainDealConditionsError ||
      amTemplateDocumentError ||
      activeSubmitByAmManagerError,
    showGuaranteesRequestError
  );

  useEffect(() => {
    if (isAmAccessDenied) {
      setModalAlert({ text: MANAGER_APPROVAL_LETTER_ACCESS_DENIED_TEXT });
    }
  }, [isAmAccessDenied, setModalAlert]);

  return {
    applicationId,
    contragentId,
    currentRouteApplication,
    effectiveApplicationId,
    coverageTypeOption: isCoverageTypeLocked
      ? MANAGER_APPROVAL_LETTER_COVERED_COVERAGE_TYPE_OPTION
      : coverageTypeOption,
    coverageTypeOptions: MANAGER_APPROVAL_LETTER_COVERAGE_TYPE_OPTIONS,
    handleCloseAlertModal,
    handleCloseAmAccessModal,
    handleCloseSuccessModal,
    handleCoverageTypeChange,
    handleMainDealConditionsDirtyChange,
    handleMainDealConditionsSubmit,
    handleSubmitByAmManager,
    headerProps,
    isAmAccessDenied,
    isAmAccessLoading,
    isApplicationLoading,
    isCoverageTypeLocked,
    isMainDealConditionsLoading,
    isMainDealConditionsSaving,
    isSubmitDisabled,
    isSubmittingByAmManager,
    mainDealConditions,
    modalAlert,
    modalSuccess,
    redirectPath,
    setModalAlert,
    showGuaranteesRequestError,
    showCoverageTypeField: shouldSubmitApplicationByAmManager,
  };
};
